/**
 * Phase 5: customers, loyalty, feedback and reports. Every rule is a setting
 * (Settings → Customers & reports); these are the defaults.
 */
export const DEFAULT_CRM_SETTINGS = Object.freeze({
  loyalty: Object.freeze({
    enabled: true,
    earnPerRupees: 100, // ₹ spent for 1 point
    pointValuePaise: 100, // 1 point = ₹1
    minRedeemPoints: 100, // balance needed before points can be used
    expiryMonths: 12, // 0 = never expire
  }),
  segments: Object.freeze({
    newDays: 30, // first visit within N days
    lapsedDays: 60, // no visit for N days
    regularVisits: 3, // at least N visits (and not lapsed)
    highValueSpendPaise: 500000, // total spend ≥ ₹5,000
  }),
  feedback: Object.freeze({ enabled: true, notifyBelow: 3 }),
  reports: Object.freeze({ dailySummaryEmail: true, dailySummaryHour: 23 }),
});

export const SEGMENTS = Object.freeze([
  { key: 'new', label: 'New', tone: 'info' },
  { key: 'regular', label: 'Regular', tone: 'success' },
  { key: 'high_value', label: 'High value', tone: 'brand' },
  { key: 'lapsed', label: 'Lapsed', tone: 'warning' },
  { key: 'occasional', label: 'Occasional', tone: 'neutral' },
]);

export const LOYALTY_TYPES = Object.freeze({
  earn: 'Earned',
  redeem: 'Redeemed',
  expire: 'Expired',
  adjust: 'Adjusted',
  reverse: 'Reversed',
});

export const DUE_PAYMENT_MODES = Object.freeze(['cash', 'upi', 'card', 'wallet']);

/**
 * Reports (spec §4.8). needs: plan feature the report depends on.
 * kind 'table' (rows) — every report also has summary figures for the thermal printout.
 */
export const REPORT_CATALOG = Object.freeze([
  {
    id: 'sales-summary',
    group: 'Sales',
    title: 'Sales summary',
    description: 'Day by day: bills, discounts, GST, total, voids, refunds.',
  },
  {
    id: 'bill-register',
    group: 'Sales',
    title: 'Bill register',
    description: 'Every bill with its amounts, payments and status.',
  },
  { id: 'items', group: 'Sales', title: 'Item-wise sales', description: 'Quantity and value per menu item and size.' },
  { id: 'categories', group: 'Sales', title: 'Category-wise sales', description: 'Sales per menu category.' },
  { id: 'addons', group: 'Sales', title: 'Add-ons', description: 'How often each add-on was chosen.' },
  { id: 'order-types', group: 'Sales', title: 'Order types', description: 'Dine-in, takeaway and parcel.' },
  { id: 'sources', group: 'Sales', title: 'Order sources', description: 'Counter, Swiggy, Zomato, phone…' },
  { id: 'payments', group: 'Sales', title: 'Payment modes', description: 'Cash, UPI, card, wallet, credit, points.' },
  { id: 'hourly', group: 'Sales', title: 'Hourly sales', description: 'Sales by hour of the day.' },
  { id: 'weekdays', group: 'Sales', title: 'Day-of-week sales', description: 'Average day by weekday.' },
  { id: 'staff', group: 'Sales', title: 'Cashier / staff', description: 'Bills and sales per staff member.' },
  { id: 'discounts', group: 'Control', title: 'Discounts', description: 'Bills with item, bill or coupon discounts.' },
  {
    id: 'voids',
    group: 'Control',
    title: 'Voids, cancellations & refunds',
    description: 'Every reversed sale with its reason.',
  },
  {
    id: 'gst-rates',
    group: 'GST',
    title: 'GST by rate',
    description: 'Taxable value and CGST/SGST/IGST/cess per rate, net of credit notes.',
  },
  { id: 'gst-hsn', group: 'GST', title: 'GST by HSN', description: 'HSN/SAC summary for GSTR-1.' },
  { id: 'shifts', group: 'Cash', title: 'Shifts', description: 'Every shift with expected and counted cash.' },
  { id: 'expenses', group: 'Cash', title: 'Expenses (cash out)', description: 'Petty expenses by category.' },
  {
    id: 'dues',
    group: 'Customers',
    title: 'Customer dues',
    description: 'Outstanding credit per customer.',
    needs: 'crm',
  },
  {
    id: 'customers',
    group: 'Customers',
    title: 'Customers & loyalty',
    description: 'Visits, spend and points per customer.',
    needs: 'crm',
  },
  {
    id: 'feedback',
    group: 'Customers',
    title: 'Feedback',
    description: 'Ratings and comments from receipts.',
    needs: 'crm',
  },
  {
    id: 'consumption',
    group: 'Inventory',
    title: 'Consumption vs recipes',
    description: 'Actual use against what recipes say you sold.',
    needs: 'inventory',
  },
  {
    id: 'stock-ledger',
    group: 'Inventory',
    title: 'Stock ledger',
    description: 'Every stock movement.',
    needs: 'inventory',
  },
  {
    id: 'wastage',
    group: 'Inventory',
    title: 'Wastage',
    description: 'Wasted stock by item and reason.',
    needs: 'inventory',
  },
  {
    id: 'purchases',
    group: 'Inventory',
    title: 'Purchases',
    description: 'Goods received with input GST.',
    needs: 'inventory',
  },
  {
    id: 'suppliers',
    group: 'Inventory',
    title: 'Supplier-wise purchases',
    description: 'Purchases per supplier.',
    needs: 'inventory',
  },
  {
    id: 'pnl',
    group: 'Profit',
    title: 'Profit & loss (simple)',
    description: 'Sales − cost of goods − wastage − expenses.',
  },
]);

export const REPORT_IDS = Object.freeze(REPORT_CATALOG.map((r) => r.id));
